import { createContext, lazy, useEffect, useState } from 'react';
import './App.css';
import { I18nextProvider, useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectIsAdmin,
  selectIsAuthenticated,
  selectUserRole,
} from './redux/selectors/auth.selector';
import { checkMandatoryVersion } from './redux/actions/desktopApp/desktopApp.actions';
import GimIntroSteps from './components/gim-components/GimIntroSteps';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router';

import ThemedRoute from './components/routing-components/ThemedRoute';
import AuthRoute from './components/routing-components/AuthRoute';
import PublicCredentialRoute, {
  lazyPage,
} from './components/routing-components/PublicCredentialRoute';
import { routePaths } from './shared/config/globalConfig';

import './styles/style.scss';
import './i18n';
import 'intro.js/introjs.css';
import AuthInitializer from './components/auth-components/AuthInitializer';
import { useGIMmanagerConnection } from './hooks/useGimManagerConnection';
import {
  managerReadySelector,
  GIMmanagerUpdateSelector,
} from './redux/selectors/desktopApp.selector';
import { useGLTF } from '@react-three/drei';
import { HubConnection } from '@microsoft/signalr';

/**
 * @template T @typedef {import('react').Context<T>} Context
 */
/**
 * @template T @typedef {import('react').Dispatch<T>} Dispatch
 */
/**
 * @template T @typedef {import('react').SetStateAction<T>} SetStateAction
 */

/**
 * @typedef {import('../global').AppContextState} AppContextState
 */

const Home = lazy(() => import('./pages/Home'));
const AboutUs = lazy(() => import('./pages/Home/Pages/AboutUs'));
const Contact = lazy(() => import('./pages/Home/Pages/Contact'));
const Vacancy = lazy(() => import('./pages/Home/Pages/Vacancy'));
const WerkenBijGIM = lazy(() => import('./pages/Home/Pages/WerkenBijGim'));
const InternshipMarketing = lazy(
  () => import('./pages/Home/Pages/InternshipMarketing')
);
const InternshipDevelopment = lazy(
  () => import('./pages/Home/Pages/InternshipDevelopment')
);
const PrivacyPolicy = lazy(() => import('./pages/Home/Pages/PrivacyPolicy'));
const FAQs = lazy(() => import('./pages/Home/Pages/FAQs'));
const Iframe = lazy(() => import('./pages/Iframe'));
const IframeMap = lazy(() => import('./pages/IframeMap'));

const { Page: StoreApp, load: loadStoreApp } = lazyPage(
  () => import('./pages/StoreApp')
);
const { Page: ProApp, load: loadProApp } = lazyPage(
  () => import('./pages/ProApp')
);
const ObjectRedirect = lazy(() => import('./pages/ObjectRedirect'));
//const TransferApp = lazy(() => import('./pages/TransferApp'));
const { Page: MapsApp, load: loadMapsApp } = lazyPage(
  () => import('./pages/MapsApp')
);

const MyProjects = lazy(() => import('./pages/MyProjects'));

/**
 * THESE DOWN HERE SHOULD BE REWORKED
 */
// const Gim = lazy(() => import('./pages/Gim'));
// const GimMaps = lazy(() => import('./pages/Gim/gimMap'));
// const StoreInfo = lazy(() => import('./pages/Gim/pages/StoreInfo'));
// const StoreTech = lazy(() => import('./pages/Gim/pages/StoreTech'));
// const ProInfo = lazy(() => import('./pages/Gim/pages/ProInfo'));
// const ProTech = lazy(() => import('./pages/Gim/pages/ProTech'));
// const MapsInfo = lazy(() => import('./pages/Gim/pages/MapsInfo'));
// const MapsTech = lazy(() => import('./pages/Gim/pages/MapsTech'));

const NotFound = lazy(() => import('./components/shared-components/NotFound'));

/**
 * Enable these back when worked
 */

// const Verify = lazy(() => import('./pages/Verify'));
// const InvoiceList = lazy(() => import('./pages/InvoiceList'));
// const ProjectOverview = lazy(() => import('./pages/ProjectOverview'));

// const Projects = lazy(() => import('./pages/Projects/projectList'));
// const TestResult = lazy(() => import('./pages/TestResult'));

/**
 * @type {Context<{appContext: AppContextState, setAppContext: Dispatch<SetStateAction<AppContextState>>}>}
 */
export const AppContext = createContext({
  appContext: null,
  setAppContext: (newState) => {},
});

/**
 * @type {Context<{t: import('i18next').TFunction<"translation", undefined>, i18n: import('i18next').i18n}>}
 */
export const TranslationContext = createContext({
  t: null,
  i18n: null,
});

/**
 *
 */
export const GIMmanagerConnectionContext = createContext({
  connection: /** @type {HubConnection} */ (null),
});

/**
 *
 * @returns {import('react').ReactElement}
 */
function App() {
  // Keep Redux in sync with the desktop app on every route
  const { connection } = useGIMmanagerConnection();

  /**
   * Translation, will be needed to translate most elements
   */
  const { t, i18n } = useTranslation();
  /**
   * user info
   */
  const isAuthenticated = useSelector(selectIsAuthenticated);

  const isAdmin = useSelector(selectIsAdmin);
  const userRole = useSelector(selectUserRole);
  const isGimConnected = useSelector(managerReadySelector);
  const appUpdateAvailable = useSelector(GIMmanagerUpdateSelector);
  /**
   * dispatch for redux
   */
  const dispatch = useDispatch();
  const [appContext, setAppContext] = useState({
    showAppLoader: false,
    showIntro: false,
    connectionCount: 0,
    isUpdateMandatory: false,
  });

  /**
   * checking latest mandatory app version
   */
  useEffect(() => {
    if (!isGimConnected || !appUpdateAvailable) return;
    const id = setTimeout(() => {
      dispatch(checkMandatoryVersion())
        .then((res) => {
          const mandatory = !!res?.data?.isUpdateMandatory;
          if (mandatory) {
            toast.info(t('Gim.mandatoryUpdate'), {
              position: 'top-right',
              autoClose: false,
            });
          }
          // Optional: persist to Redux if you want:
          // dispatch({ type: DESKTOP_APP_UPDATE_MANDATORY, payload: mandatory });
        })
        .catch((error) => console.log('error:', error));
    }, 1500);
    return () => clearTimeout(id);
  }, [isGimConnected, appUpdateAvailable, dispatch, t]);

  return (
    <TranslationContext.Provider value={{ t, i18n }}>
      {document.querySelector('#GimStore') !== null && (
        <GimIntroSteps
          isGimConnected={isGimConnected}
          isAuthenticated={isAuthenticated}
        />
      )}

      <I18nextProvider i18n={i18n}>
        <GIMmanagerConnectionContext.Provider
          value={{ connection: connection }}
        >
          <AuthInitializer />
          <BrowserRouter>
            <Routes>
              <Route
                path={`${routePaths.iFrame}/:lang/:PID?/:OID?`}
                element={<ThemedRoute component={Iframe} noLayout />}
              />{' '}
              <Route
                path={`${routePaths.iFrameMap}`}
                element={<ThemedRoute component={IframeMap} />}
              />{' '}
              <Route
                path={routePaths.root}
                element={<ThemedRoute component={Home} />}
              />
              <Route
                path={`${routePaths.home}`}
                element={<ThemedRoute component={Home} />}
              />
              <Route
                path={`${routePaths.aboutUs}`}
                element={<ThemedRoute component={AboutUs} />}
              />
              <Route
                path={`${routePaths.contact}`}
                element={<ThemedRoute component={Contact} />}
              />
              <Route
                path={`${routePaths.vacancy}`}
                element={<ThemedRoute component={Vacancy} />}
              />
              <Route
                path={`${routePaths.werkenBijGim}`}
                element={<ThemedRoute component={WerkenBijGIM} />}
              />
              <Route
                path={`${routePaths.internshipMarketing}`}
                element={<ThemedRoute component={InternshipMarketing} />}
              />
              <Route
                path={`${routePaths.internshipDevelopment}`}
                element={<ThemedRoute component={InternshipDevelopment} />}
              />
              <Route
                path={`${routePaths.privacyPolicy}`}
                element={<ThemedRoute component={PrivacyPolicy} />}
              />
              <Route
                path={routePaths.faqs}
                element={<ThemedRoute component={FAQs} />}
              />
              {/* generic object redirect route */}
              <Route
                path={routePaths.storeApp}
                element={
                  <Navigate to={routePaths.store} replace relative="route" />
                }
              />
              <Route
                path={routePaths.proApp}
                element={<ThemedRoute component={ObjectRedirect} />}
              />
              <Route
                path={routePaths.object}
                element={<ThemedRoute component={ObjectRedirect} />}
              />
              <Route
                path={`${routePaths.object}/:PID?`}
                element={<ThemedRoute component={ObjectRedirect} />}
              />
              <Route
                path={`${routePaths.object}/:PID?/:OID?`}
                element={<ThemedRoute component={ObjectRedirect} />}
              />
              {/* gim pro routes */}
              <Route
                path={routePaths.pro}
                element={
                  <PublicCredentialRoute
                    component={ProApp}
                    load={loadProApp}
                  />
                }
              />
              <Route
                path={`${routePaths.pro}/:PID?`}
                element={
                  <PublicCredentialRoute
                    component={ProApp}
                    load={loadProApp}
                  />
                }
              />
              <Route
                path={`${routePaths.pro}/:PID?/:OID?`}
                element={
                  <PublicCredentialRoute
                    component={ProApp}
                    load={loadProApp}
                  />
                }
              />
              {/* gim store routes */}
              <Route
                path={routePaths.store}
                element={
                  <PublicCredentialRoute
                    component={StoreApp}
                    load={loadStoreApp}
                  />
                }
              />
              <Route
                path={`${routePaths.store}/:PID?`}
                element={
                  <PublicCredentialRoute
                    component={StoreApp}
                    load={loadStoreApp}
                  />
                }
              />
              <Route
                path={`${routePaths.store}/:PID?/:OID?`}
                element={
                  <PublicCredentialRoute
                    component={StoreApp}
                    load={loadStoreApp}
                  />
                }
              />
              {/* gimmaps route */}
              <Route
                path={routePaths.maps}
                element={
                  <PublicCredentialRoute
                    component={MapsApp}
                    load={loadMapsApp}
                  />
                }
              />
              <Route
                path={routePaths.projects}
                element={<AuthRoute component={MyProjects} />}
              />
              {/* <ThemedRoute path={routePaths.veryfyProject} component={Verify} />
              <ThemedRoute
                path={routePaths.storeInfo}
                exact
                component={StoreInfo}
              />
              <ThemedRoute
                path={routePaths.storeTech}
                exact
                component={StoreTech}
              />
              <ThemedRoute
                path={routePaths.proInfo}
                exact
                component={ProInfo}
              />
              <ThemedRoute
                path={routePaths.proTech}
                exact
                component={ProTech}
              />
              <ThemedRoute
                path={`${routePaths.mapsApp}`}
                exact
                component={GimMaps}
              />
              <ThemedRoute
                path={routePaths.mapsInfo}
                exact
                component={MapsInfo}
              />
              <ThemedRoute
                path={routePaths.mapsTech}
                exact
                component={MapsTech}
              />
              <ThemedRoute
                path={routePaths.testResult}
                exact
                component={TestResult}
              />
              <AuthAdminRoute
                path={routePaths.invoiceList}
                component={InvoiceList}
                isAuthenticated={isAuthenticated}
                isAdmin={isAdmin}
                userRole={userRole}
                exact
              />
              <AuthAdminRoute
                path={routePaths.projectOverview}
                component={ProjectOverview}
                isAuthenticated={isAuthenticated}
                isAdmin={isAdmin}
                userRole={userRole}
                exact
              />
              <AuthRoute
                path={routePaths.projects}
                exact
                component={Projects}
                isAuthenticated={isAuthenticated}
              /> */}
              <Route element={<ThemedRoute path="*" component={NotFound} />} />
            </Routes>
          </BrowserRouter>
        </GIMmanagerConnectionContext.Provider>
      </I18nextProvider>
    </TranslationContext.Provider>
  );
}

export default App;
